import React from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { TargetDriftStatus } from 'redux/slices/pipelines/constants/targetDriftStatus';

// deploy-pipelines-run-provenance (Wave 2) AC-6/AC-7/AC-8/AC-9 — the target-drift tri-state badge.
// Retires exactly one clause of Wave 1's AC-25 (the "target drift" pin).
//
// AC-9, all three clauses are hard requirements:
//   • state is NEVER conveyed by colour alone — every state pairs a distinct icon AND a text label;
//   • every colour resolves from the MUI theme via the Chip `color` prop (the same semantic-colour
//     route PipelineRunStatusChip already takes), so dark mode and user-selectable ColorSets palettes
//     both work — there is ZERO hardcoded hex/rgba in this file;
//   • Unknown is styled `default` (neutral), NOT error/warning: it is an expected, honest state
//     (spec F3 names three reachable causes), not a fault.
//
// AC-8: the tri-state is derived server-side and Unknown is the BE enum's 0/default, so an absent or
// unrecognised value lands on the Unknown presentation here too — never a false "In sync".
//
// AC-16 / TC-DPRP-024 Step 4: this badge conveys IMAGE-SYNC status only. It deliberately carries no
// environment/deployment-tier claim ("UAT"/"PROD") — that remains a Wave-1 negative pin in force.
const DRIFT_META = {
	[TargetDriftStatus.InSync]: { label: 'In sync', color: 'success', Icon: CheckCircleOutlineIcon },
	[TargetDriftStatus.Drifted]: { label: 'Drifted', color: 'warning', Icon: SyncProblemIcon },
	[TargetDriftStatus.Unknown]: { label: 'Unknown', color: 'default', Icon: HelpOutlineIcon }
};

const TargetDriftBadge = ({ status, deployedTagVersion, latestTagVersion }) => {
	const meta = DRIFT_META[status] ?? DRIFT_META[TargetDriftStatus.Unknown];
	const Icon = meta.Icon;

	// AC-7 — "names what it has drifted from in terms the reader can act on". Rendered ONLY when the
	// state is Drifted AND both tag versions actually resolved, so this can never print "tag
	// undefined"; when either is unknown the badge stands alone rather than asserting a half-fact.
	const showDriftDetail =
		status === TargetDriftStatus.Drifted &&
		deployedTagVersion !== undefined &&
		deployedTagVersion !== null &&
		latestTagVersion !== undefined &&
		latestTagVersion !== null;

	return (
		<Box display="flex" flexDirection="column" gap={0.5} minWidth={0}>
			<Box>
				<Chip size="small" variant="outlined" color={meta.color} icon={<Icon fontSize="small" />} label={meta.label} />
			</Box>
			{showDriftDetail && (
				<Typography variant="caption" color="text.secondary">
					Running tag {deployedTagVersion} · latest build is tag {latestTagVersion}
				</Typography>
			)}
		</Box>
	);
};

export default React.memo(TargetDriftBadge);

TargetDriftBadge.propTypes = {
	status: PropTypes.number,
	deployedTagVersion: PropTypes.number,
	latestTagVersion: PropTypes.number
};
